import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { ModalController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { PanAppState, PlatformService, Tracking } from '@panamax/app-state';
import {
  debounceTime,
  filter,
  map,
  mergeMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { OrderAnalyticsMessages } from '../order/tracking/analytics/order-analytics.constants';
import { DyfProductInfo, DyfViewModel } from '../order/models/dyf-view-model';
import { PoPopoverComponent } from '../order/components/purchase-order/po-popover-desktop/po-popover.component';
import { Crumb } from '../shared/components/breadcrumbs/breadcrumbs.component';
import {
  EllipsisClickData,
  ProductClickData,
} from '../shared/models/usf-product-card.model';
import { DyfProductCardService } from '../shared/services/product-card/dyf-product-card.service';
import { DidYouForgetService } from './services/did-you-forget.service';
import { InspiredPicksProductInfo } from '@app/order/models/inspired-picks-view-model';
import {
  combineLatest,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
} from 'rxjs';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { TranslateService } from '@ngx-translate/core';
import { MerchZoneProductInfo } from '@order/models/merch-zone-view-model';
import { ReviewOrderMerchZone } from '@shared/constants/merchandising-constants';
import { TrackingTypeEnum } from '@app/did-you-forget/tracking/shared/models/tracking-type-enum';
import { DidYouForgetCategory } from './constants/did-you-forget-category.enum';
import { MonetateAnalyticsService } from '@shared/services/analytics/monetate-analytics.service';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';

@Component({
  selector: 'app-did-you-forget',
  templateUrl: './did-you-forget.page.html',
  styleUrls: ['./did-you-forget.page.scss'],
})
export class DidYouForgetPage implements OnInit, ViewWillEnter, ViewWillLeave {
  breadcrumbs: Crumb[];
  // Observable with the did you forget vm
  dyf$: Observable<DyfViewModel>;
  variant: ReviewOrderMerchZone;
  // Observable with pipeline to only fire once and use it for tracking
  tracking$: Observable<DyfViewModel>;
  DidYouForgetCategory = DidYouForgetCategory;

  OrderAnalyticsMessages = OrderAnalyticsMessages;

  merchZoneSeenProductSet: Set<string>;
  merchZoneSeenProductSubject$: Subject<string>;
  merchZoneSeenProductObserver: IntersectionObserver;
  merchZoneSeenProductsSub$: Subscription;
  originSearchPage = SEARCH_PAGES_ENUM.didYouForget;

  constructor(
    readonly platformService: PlatformService,
    private didYouForgetService: DidYouForgetService,
    public modalController: ModalController,
    private route: ActivatedRoute,
    private router: Router,
    private dyfProductCardService: DyfProductCardService,
    readonly panAppState: PanAppState,
    private translateService: TranslateService,
    private monetateAnalyticsService: MonetateAnalyticsService,
  ) {}

  ngOnInit() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.didYouForget',
        url: '',
        active: true,
      },
    ];
  }

  ionViewWillEnter() {
    this.merchZoneSeenProductSet = new Set();
    this.merchZoneSeenProductSubject$ = new ReplaySubject<string>();
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.didYouForget),
    );

    this.dyf$ = this.didYouForgetService
      .loadDyfViewModel$()
      .pipe(
        takeUntil(
          this.router.events.pipe(filter(e => e instanceof NavigationStart)),
        ),
      );

    this.dyf$
      .pipe(
        debounceTime(1000),
        mergeMap(dyfVm => {
          return combineLatest([
            of(dyfVm),
            of(this.didYouForgetService.allProductNumbers(dyfVm)),
          ]).pipe();
        }),
        filter(([_vm, productNumbers]) => !!productNumbers?.length),
        map(([vm, _loaded]) => vm),
        take(1),
      )
      .subscribe(dyfVm => {
        this.variant = dyfVm.merchZone?.variant;

        if (
          dyfVm?.merchZone?.pageTitle &&
          !dyfVm.products?.length &&
          !dyfVm.inspiredPicks?.products?.length &&
          !!dyfVm.merchZone?.products?.length
        ) {
          this.panAppState.setPageTitle(
            this.translateService.instant(dyfVm?.merchZone?.pageTitle),
          );
        }

        if (
          !dyfVm.products?.length &&
          !dyfVm.inspiredPicks?.products?.length &&
          !!dyfVm?.merchZone?.products?.length
        ) {
          this.originSearchPage =
            this.variant === ReviewOrderMerchZone.trendingNowVariant
              ? SEARCH_PAGES_ENUM.trendingNow
              : SEARCH_PAGES_ENUM.topSellers;
          this.setUpMonetateAnalytics();
        }

        this.didYouForgetService.trackPageLoad(dyfVm);
      });
  }

  ionViewWillLeave() {
    if (this.merchZoneSeenProductObserver) {
      this.merchZoneSeenProductObserver.disconnect();
    }
    if (this.merchZoneSeenProductsSub$) {
      this.merchZoneSeenProductsSub$.unsubscribe();
    }
  }

  routeTo(path: string, queryParams = {}) {
    this.router.navigate([path], { queryParams, relativeTo: this.route });
  }

  async presentChangePOModal() {
    const popover = await this.modalController.create({
      component: PoPopoverComponent,
      cssClass: 'modal-desktop-size-sm',
      mode: 'md',
    });

    return await popover.present();
  }

  mapTrackingData(event, featureName: string): Tracking {
    let merchFeature;
    merchFeature =
      this.variant === ReviewOrderMerchZone.topSellerVariant
        ? TrackingTypeEnum.topSellers
        : this.variant === ReviewOrderMerchZone.trendingNowVariant
          ? TrackingTypeEnum.trendingNow
          : '';
    featureName = featureName === '' ? merchFeature : featureName;
    const trackingData: Tracking = {
      analytics: {
        data: {
          products: [
            {
              addMethod: featureName,
              merchFeatureName: featureName,
              attributes: event.product.trackingAttributes,
            },
          ],
        },
      },
    };
    if (!!event?.alternative?.product) {
      if (event.quantityUpdate.productNumber === event.product.productNumber) {
        trackingData.analytics.data.products[0].orderedOriginal = '1';
      } else {
        trackingData.analytics.data.products[0].subIn = '1';
        trackingData.analytics.data.products[0].substitutionParent = `${event.product.summary.divisionNumber}-${event.product.productNumber}`;
        (trackingData.analytics.data.products as any[]).push({
          attributes: event.product.trackingAttributes,
          divisionApn: `${event.product.summary.divisionNumber}-${event.product.productNumber}`,
          subOut: '1',
          merchFeatureName: featureName,
        });
      }
    }

    return trackingData;
  }

  async changeQuantityHandler(event: any, featureName: string) {
    const trackingData: Tracking = this.mapTrackingData(event, featureName);
    this.didYouForgetService.changeQuantity(
      event.quantityUpdate.productNumber,
      event.quantityUpdate.quantity,
      event.quantityUpdate.isEaches,
      trackingData,
    );
  }

  async routeToPDP(
    productClickData: ProductClickData,
    productInfo:
      | DyfProductInfo
      | InspiredPicksProductInfo
      | MerchZoneProductInfo,
    dyfCategory?: DidYouForgetCategory,
  ) {
    const rank = 'rank' in productInfo ? productInfo.rank : null;
    const recToken = 'recToken' in productInfo ? productInfo.recToken : null;
    if (!!recToken) {
      this.monetateAnalyticsService
        .postClickToMonetate(recToken)
        .pipe(take(1))
        .subscribe();
    }
    this.dyfProductCardService.routeToPDP(
      productClickData.productNumber,
      productClickData.isQuickView,
      rank,
      dyfCategory,
      this.variant,
    );
  }

  openProductCardEllipsis(
    ellipsisClickData: EllipsisClickData,
    productInfo:
      | DyfProductInfo
      | InspiredPicksProductInfo
      | MerchZoneProductInfo,
    dyfCategory?: DidYouForgetCategory,
  ) {
    const rank = 'rank' in productInfo ? productInfo.rank : null;
    const buttons = this.dyfProductCardService.constructEllipsisButtons(
      ellipsisClickData,
      rank,
      dyfCategory,
      this.variant,
      this.originSearchPage,
    );
    this.dyfProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  clickPromotions(event: EllipsisClickData) {
    this.dyfProductCardService.openQuickViewModal(
      event,
      'productStatus',
      this.originSearchPage,
    );
  }

  async openProductStatus(ellipsisClickData: EllipsisClickData) {
    this.dyfProductCardService.showProductStatuses(ellipsisClickData);
  }

  trackByProductNumber = (
    index: number,
    id: DyfProductInfo | InspiredPicksProductInfo | MerchZoneProductInfo,
  ) => id?.product?.productNumber;

  setUpMonetateAnalytics() {
    this.merchZoneSeenProductObserver?.disconnect();
    this.merchZoneSeenProductsSub$?.unsubscribe();

    this.merchZoneSeenProductsSub$ = this.monetateAnalyticsService
      .getSeenProductObservable(this.merchZoneSeenProductSubject$)
      .subscribe(recTokens =>
        this.monetateAnalyticsService
          .postImpressionsToMonetate(recTokens)
          .pipe(take(1))
          .subscribe(),
      );
  }

  trackMerchZoneImpression(productCardRef: ElementRef) {
    if (!this.merchZoneSeenProductSet.has(productCardRef.nativeElement.id)) {
      this.monetateAnalyticsService.logProductAsSeen(
        this.merchZoneSeenProductSubject$,
        this.merchZoneSeenProductSet,
        productCardRef.nativeElement.id,
      );
    }
  }
}
